<template>
  <div v-if="showRow">
    <v-divider v-if="showDivider" class="my-5" />
    <v-row>
      <v-col cols="12" md="6">
        <v-list-subheader class="ma-0 pa-0" inset>
          {{ $t('userManagement.accessRight') }}
        </v-list-subheader>
        <BMSelect
          v-if="!currentPermission.roleId"
          :items="convertedAvailableRoles"
          :label="$t('userManagement.roleSelectLabel')"
          identifier="permission-row-role-select"
          name="permission-role"
          data-cc="permission-row-role-select"
          @update:modelValue="currentPermission.roleId = $event"
        />
        <div class="d-flex align-center mt-3" v-else>
          <v-icon-btn
            size="small"
            color="primary"
            icon="$trash"
            variant="text"
            @click="$emit('deletePermission')"
          />
          <span class="ml-1">{{ roleName }}</span>
        </div>
      </v-col>

      <v-col cols="12" md="6">
        <v-list-subheader class="ma-0 pa-0" inset>
          {{ $t('userProfile.scope') }}
        </v-list-subheader>
        <BMMultiSelect
          v-if="currentPermission.roleId"
          class="pt-1"
          :modelValue="currentPermission.scopes"
          :items="availableScopes"
          :label="$t('permissionManagement.scopeSelection.searchLabel')"
          rtl
          @update:modelValue="scopesChanged"
          identifier="permission-row-scope-select"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import constants from '@/config/constants';
import BMSelect from '@/components/common/utilities/table/BMSelect.vue';
import UserManagementService from '@/services/userManagement/UserManagementService';
import BMMultiSelect from '@/components/common/utilities/table/BMMultiSelect.vue';
import { isEqual, capitalize } from 'lodash';
import useScopesStore from '@/stores/scopes';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'PermissionRow',
  components: {
    BMSelect,
    BMMultiSelect
  },
  props: {
    permission: {
      type: Object,
      required: true
    },
    allPermissions: {
      type: Array,
      required: true
    },
    showDivider: {
      type: Boolean,
      default: false
    },
    hasSucoInPermissions: {
      type: Boolean,
      default: false
    },
    selectedSubcontractor: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  setup() {
    const scopesStore = useScopesStore();
    const { depotPath, currentSelectedPath, hasActionForPath } =
      usePermissions();

    return {
      availableRolesAndScopes: scopesStore.availableRolesAndScopes,
      scopeTree: scopesStore.scopeTree,
      selectedScope: scopesStore.selectedScope,
      depotPath,
      currentSelectedPath,
      hasActionForPath
    };
  },
  data() {
    return {
      currentPermission: {},
      availableScopes: []
    };
  },
  computed: {
    isAdminScope() {
      return (
        this.selectedScope?.type === constants.scopeTypes.ADMIN ||
        !this.selectedScope?.type
      );
    },
    isDepotScope() {
      return (
        this.selectedScope?.type === constants.scopeTypes.DISTRIBUTION_CENTER
      );
    },
    isTenantScope() {
      return this.selectedScope?.type === constants.scopeTypes.TENANT;
    },
    isProfilePage() {
      return this.selectedScope?.type === constants.scopeTypes.PROFILE;
    },
    showRow() {
      return (
        this.availableRolesAndScopes?.roles?.find(
          ({ id }) => id === this.currentPermission.roleId
        ) || !this.currentPermission.roleId
      );
    },
    availableRoles() {
      const { rolesContexts } = constants;
      const availableRoles = this.availableRolesAndScopes?.roles || [];
      const isSucoContext = (context) =>
        context === rolesContexts.SUBCONTRACTOR;
      const isDepotOrSucoContext = (context) =>
        [rolesContexts.SUBCONTRACTOR, rolesContexts.DEPOT].includes(context);

      let filteredRoles = availableRoles;

      if (this.hasSucoInPermissions || this.selectedSubcontractor) {
        filteredRoles = availableRoles.filter(({ context }) =>
          isSucoContext(context)
        );
      } else if (this.allPermissions.length > 1) {
        filteredRoles = availableRoles.filter(
          ({ context }) => !isSucoContext(context)
        );
      }

      const allPermissionsIds = this.allPermissions.map(({ roleId }) => roleId);
      filteredRoles = filteredRoles.filter(
        ({ id }) => !allPermissionsIds.includes(id)
      );

      if (this.isAdminScope || this.isProfilePage) {
        return filteredRoles;
      }

      if (this.isDepotScope) {
        const [tenantScope] = this.depotPath.split('.');
        return filteredRoles.filter(
          ({ context, visibilityPath }) =>
            isDepotOrSucoContext(context) && tenantScope === visibilityPath
        );
      }

      if (this.isTenantScope) {
        const tenantScope = this.currentSelectedPath;
        return filteredRoles.filter(
          ({ visibilityPath, context }) =>
            tenantScope === visibilityPath ||
            (visibilityPath === '' &&
              context === constants.rolesContexts.TENANT)
        );
      }

      return [];
    },
    roleContext() {
      const currentRoleId = this.currentPermission.roleId;
      const availableRoles = this.availableRolesAndScopes?.roles;
      if (!currentRoleId || !availableRoles) return null;
      return availableRoles.find((role) => role.id === currentRoleId)?.context;
    },
    convertedAvailableRoles() {
      const convertedRoles = this.availableRoles.map((role) =>
        this.mapRoleToConvertedFormat(role)
      );

      return convertedRoles.sort((a, b) => this.sortConvertedRoles(a, b));
    },
    roleName() {
      return this.currentFullInfoRole
        ? this.getFullRoleName(
            this.currentFullInfoRole.name,
            this.currentFullInfoRole.visibilityPath,
            this.currentFullInfoRole?.context
          )
        : null;
    },
    currentFullInfoRole() {
      return this.availableRolesAndScopes?.roles.find(
        ({ id }) => id === this.currentPermission.roleId
      );
    },
    flattenScopeTree() {
      return UserManagementService.flattenScopeTree(this.scopeTree);
    }
  },
  methods: {
    scopesChanged(scopes) {
      const allTenantsAdminWasSelected =
        this.roleContext === constants.rolesContexts.TENANT &&
        scopes.includes('');
      this.currentPermission.scopes = allTenantsAdminWasSelected
        ? ['']
        : [...scopes];
    },
    mapRoleToConvertedFormat(role) {
      const { id, name, visibilityPath, context } = role;
      return {
        title: this.getFullRoleName(name, visibilityPath, context),
        value: id,
        visibilityPath
      };
    },
    sortConvertedRoles(a, b) {
      if (!a.visibilityPath) return -1;
      if (!b.visibilityPath) return 1;
      return a.title.localeCompare(b.title);
    },
    getFullRoleName(role, visibilityPath, context) {
      const addContext = context
        ? `(${capitalize(context.toLowerCase())})`
        : '';
      return visibilityPath && (this.isAdminScope || this.isProfilePage)
        ? `${visibilityPath
            .replaceAll('_', '-')
            .toUpperCase()}: ${role} ${addContext}`
        : `${role} ${addContext}`;
    },
    setAvailableScopes() {
      if (!this.currentPermission.roleId) {
        this.availableScopes = [];
        return;
      }

      this.availableScopes = this.getScopesByContext(this.roleContext);
      if (this.selectedSubcontractor) {
        this.availableScopes = this.availableScopes.filter(
          ({ value }) =>
            value.split('.')[4] === this.selectedSubcontractor.id.toString()
        );
      }
      this.availableScopes = this.availableScopes?.map((scope) => {
        const isRoleIdExistInAvailableRoles =
          this.availableRolesAndScopes.roles.find(
            ({ id }) => id === this.currentPermission.roleId
          );
        const currentUserCanDeletePermission = this.hasActionForPath(
          'can-assign-permission',
          scope.value
        );
        const scopeIsEnabled =
          isRoleIdExistInAvailableRoles && currentUserCanDeletePermission;
        const disabledSucos = this.isDisabledSucos(scope.value);

        return {
          disabled: !scopeIsEnabled || disabledSucos,
          ...scope
        };
      });

      this.autoSelectSingleScope();
    },
    getScopesByContext(roleContext) {
      if (this.isAdminScope || this.isProfilePage) {
        return this.getAdminScopesByContext(roleContext);
      } else if (this.isDepotScope) {
        return this.getDepotScopesByContext(roleContext);
      } else if (this.isTenantScope) {
        return this.getTenantScopesByContext(roleContext);
      }
      return [];
    },
    getAdminScopesByContext(roleContext) {
      switch (roleContext) {
        case constants.rolesContexts.ADMIN:
          return [{ value: '', title: constants.rolesContexts.ADMIN }];
        case constants.rolesContexts.TENANT:
          return this.setAdminScopesForTenantRole();
        case constants.rolesContexts.DEPOT:
          return this.setAdminScopesForDepotOrSubcontractorRole();
        case constants.rolesContexts.SUBCONTRACTOR:
          return this.setAdminScopesForDepotOrSubcontractorRole(false);
        default:
          return [];
      }
    },
    getTenantScopesByContext(roleContext) {
      switch (roleContext) {
        case constants.rolesContexts.TENANT:
          return this.setTenantScopesForTenantRole();
        case constants.rolesContexts.DEPOT:
          return this.setAdminScopesForDepotOrSubcontractorRole();
        case constants.rolesContexts.SUBCONTRACTOR:
          return this.setAdminScopesForDepotOrSubcontractorRole(false);
        default:
          return [];
      }
    },
    getDepotScopesByContext(roleContext) {
      switch (roleContext) {
        case constants.rolesContexts.DEPOT: {
          const { name: title, id: value } = this.scopeTree[0];
          return [{ value, title }];
        }
        case constants.rolesContexts.SUBCONTRACTOR: {
          const { name, children } = this.scopeTree[0];
          return children.map((subcontractor) => ({
            value: subcontractor.id,
            title: `${name} ⋙ ${subcontractor.name}`
          }));
        }
        default:
          return [];
      }
    },
    isDisabledSucos(scopeValue) {
      if (scopeValue.split('.').length === 5) {
        for (const currentScope of this.currentPermission.scopes) {
          const scopePrefix = currentScope.split('.').splice(0, 4).join('.');
          if (
            scopeValue.startsWith(scopePrefix) &&
            currentScope !== scopeValue
          ) {
            return true;
          }
        }
      } else if (this.isTenantScope) {
        switch (this.roleContext) {
          case constants.rolesContexts.TENANT:
            this.setAdminScopesForTenantRole();
            break;
          case constants.rolesContexts.DEPOT:
            this.setAdminScopesForDepotOrSubcontractorRole();
            break;
          case constants.rolesContexts.SUBCONTRACTOR:
            this.setAdminScopesForDepotOrSubcontractorRole(false);
            break;
          default:
            break;
        }
      }
      return false;
    },
    autoSelectSingleScope() {
      const [firstScope] = this.availableScopes;
      if (
        this.availableScopes.length === 1 &&
        !firstScope.disabled &&
        !this.currentPermission.scopes.length
      ) {
        this.currentPermission.scopes = [firstScope.value];
      }
    },
    autoSelectSingleRole() {
      const [firstRole] = this.availableRoles;
      if (this.availableRoles.length === 1 && !this.currentPermission.roleId) {
        this.currentPermission.roleId = firstRole.id;
      }
    },

    setAdminScopesForDepotOrSubcontractorRole(isDepotContextRole = true) {
      const filteredScopeTree = this.flattenScopeTree.filter(
        ({ id, scope }) => {
          if (!id.includes(this.currentFullInfoRole.visibilityPath))
            return false;
          return isDepotContextRole ? scope !== 'SUCO' : scope === 'SUCO';
        }
      );

      return filteredScopeTree.map(({ id, name }) => ({
        value: id,
        title: name
      }));
    },
    disableTenantsIfAdmin(tenants, hasEmptyScope) {
      if (hasEmptyScope) {
        return tenants.map((tenant) => ({
          ...tenant,
          disabled: true
        }));
      }
      return tenants;
    },
    setTenantScopesForTenantRole() {
      const hasEmptyScope = this.currentPermission.scopes.includes('');

      const tenants = this.scopeTree.map(({ id, name }) => ({
        value: id,
        title: name,
        disabled: false
      }));
      return this.disableTenantsIfAdmin(tenants, hasEmptyScope);
    },
    setAdminScopesForTenantRole() {
      const currentPermission = this.availableRolesAndScopes.roles.find(
        ({ id }) => id === this.currentPermission.roleId
      );
      const scopeTree = this.scopeTree.filter(({ id }) => {
        return (
          id === currentPermission.visibilityPath ||
          currentPermission.visibilityPath === ''
        );
      });
      const hasEmptyScope = this.currentPermission.scopes.includes('');
      const tenants = scopeTree.map(({ id, name }) => ({
        value: id,
        title: name,
        disabled: false
      }));

      const disabledTenants = this.disableTenantsIfAdmin(
        tenants,
        hasEmptyScope
      );
      if (currentPermission.visibilityPath === '')
        disabledTenants.unshift({
          value: '',
          title: `${constants.rolesContexts.TENANT} ${constants.rolesContexts.ADMIN}`
        });

      return disabledTenants;
    }
  },
  watch: {
    currentPermission: {
      handler(value) {
        this.$emit('permissionChanged', value);
        this.setAvailableScopes();
        this.autoSelectSingleRole();
      },
      deep: true
    },
    permission: {
      handler(value) {
        if (!isEqual(value, this.currentPermission)) {
          this.currentPermission = { ...value };
        }
      },
      deep: true
    }
  },
  mounted() {
    this.currentPermission = { ...this.permission };
  }
};
</script>
